import React, { useEffect, useState } from "react";
import { Box, Typography, Divider } from "@mui/material";
import FieldNumber from "../../components/Profile/FieldComponents/FieldNumber";
import FieldText from "../../components/Profile/FieldComponents/FieldText";
import FieldBoolean from "../../components/Profile/FieldComponents/FieldBoolean";
import FieldSelect from "../../components/Profile/FieldComponents/FieldSelect";
import FieldDate from "../../components/Profile/FieldComponents/FieldDate";
import FieldObject from "../../components/Profile/FieldComponents/FieldObject";
import FieldArray from "../../components/Profile/FieldComponents/FieldArray";

function humanizeLabel(key) {
  if (!key) return "";
  return key
    .toString()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Sanitize strings for view mode
function cleanString(str) {
  return String(str)
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .trim();
}

function renderValue(fieldDef) {
  // If non-object (primitive), print directly
  if (fieldDef === null || fieldDef === undefined) return "";
  if (typeof fieldDef !== "object") return String(fieldDef);

  // Prefer explicit value when provided
  if (Object.prototype.hasOwnProperty.call(fieldDef, "value")) {
    const v = fieldDef.value;
    if (v === null || v === undefined) return "";
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  }

  // No explicit value yet (schema-style). Leave blank for now.
  return "";
}

export default function ProfileInfo({
  data = {},
  editMode = false,
  onDataChange,
  // flat error map keyed by dot-path, e.g. "organization_identity.industry_classification.industry_sub_sector"
  errors = {},
}) {
  const safeData = data && typeof data === "object" ? data : {};

  const [localData, setLocalData] = useState(() => {
    try {
      return JSON.parse(JSON.stringify(safeData));
    } catch (e) {
      return safeData || {};
    }
  });

  useEffect(() => {
    try {
      const next = JSON.parse(JSON.stringify(safeData));
      setLocalData(next);
      if (onDataChange) {
        onDataChange(next);
      }
    } catch (e) {
      const next = safeData || {};
      setLocalData(next);
      if (onDataChange) {
        onDataChange(next);
      }
    }
  }, [safeData]);

  const getValueFromDef = (def) => {
    if (!def || typeof def !== "object") return "";
    if (Object.prototype.hasOwnProperty.call(def, "value")) return def.value;
    return "";
  };

  const updateValueAtPath = (path, newValue) => {
    console.log(
      `[ProfileInfo] updateValueAtPath called with path:`,
      path,
      `value:`,
      newValue
    );
    setLocalData((prev) => {
      let next;
      try {
        next = JSON.parse(JSON.stringify(prev || {}));
      } catch (e) {
        next = { ...(prev || {}) };
      }
      let node = next;
      for (let i = 0; i < path.length - 1; i++) {
        const k = path[i];
        node[k] = node[k] || {};
        node = node[k];
      }
      const lastKey = path[path.length - 1];
      node[lastKey] = node[lastKey] || {};
      node[lastKey].value = newValue;

      // If updating nested object fields, sync to parent's def.value
      if (path.length === 3 && path[1] === "fields") {
        const sectionKey = path[0];
        const fieldKey = path[2];
        const parentDef = next[sectionKey];

        console.log(
          `[ProfileInfo] Detected nested object field update. sectionKey: "${sectionKey}", fieldKey: "${fieldKey}"`
        );
        console.log(
          `[ProfileInfo] parentDef type:`,
          parentDef?.type,
          `has fields:`,
          !!parentDef?.fields
        );

        if (parentDef && parentDef.type === "object" && parentDef.fields) {
          // Build the complete object value from all field values
          const objValue =
            parentDef.value && typeof parentDef.value === "object"
              ? { ...parentDef.value }
              : {};
          objValue[fieldKey] = newValue;
          parentDef.value = objValue;
          console.log(
            `[ProfileInfo] Synced parent object value for "${sectionKey}":`,
            objValue
          );
        }
      }

      // Also handle path [sectionKey, fieldKey, "fields", ik] for nested objects within sections
      if (path.length === 4 && path[2] === "fields") {
        const sectionKey = path[0];
        const fieldKey = path[1];
        const innerFieldKey = path[3];
        const parentDef = next[sectionKey]?.[fieldKey];

        console.log(
          `[ProfileInfo] Detected deeply nested object field update. sectionKey: "${sectionKey}", fieldKey: "${fieldKey}", innerFieldKey: "${innerFieldKey}"`
        );
        console.log(
          `[ProfileInfo] parentDef type:`,
          parentDef?.type,
          `has fields:`,
          !!parentDef?.fields
        );

        if (parentDef && parentDef.type === "object" && parentDef.fields) {
          // Build the complete object value from all field values
          const objValue =
            parentDef.value && typeof parentDef.value === "object"
              ? { ...parentDef.value }
              : {};
          objValue[innerFieldKey] = newValue;
          parentDef.value = objValue;
          console.log(
            `[ProfileInfo] Synced parent object value for "${sectionKey}.${fieldKey}":`,
            objValue
          );
        }
      }

      // Handle path [sectionKey, "fields", ik, "fields", nestedKey] for nested objects within section-level objects
      if (path.length === 5 && path[1] === "fields" && path[3] === "fields") {
        const sectionKey = path[0];
        const parentFieldKey = path[2];
        const nestedFieldKey = path[4];
        const parentDef = next[sectionKey]?.fields?.[parentFieldKey];

        console.log(
          `[ProfileInfo] Detected section-level nested object field update. sectionKey: "${sectionKey}", parentFieldKey: "${parentFieldKey}", nestedFieldKey: "${nestedFieldKey}"`
        );

        if (parentDef && parentDef.type === "object" && parentDef.fields) {
          // Build the complete object value from all field values
          const objValue =
            parentDef.value && typeof parentDef.value === "object"
              ? { ...parentDef.value }
              : {};
          objValue[nestedFieldKey] = newValue;
          parentDef.value = objValue;
          console.log(
            `[ProfileInfo] Synced parent nested object value for "${sectionKey}.fields.${parentFieldKey}":`,
            objValue
          );
        }
      }

      // Handle path [sectionKey, fieldKey, "fields", ik, "fields", nestedKey] for nested objects within field-level objects
      if (path.length === 6 && path[2] === "fields" && path[4] === "fields") {
        const sectionKey = path[0];
        const fieldKey = path[1];
        const parentFieldKey = path[3];
        const nestedFieldKey = path[5];
        const parentDef =
          next[sectionKey]?.[fieldKey]?.fields?.[parentFieldKey];

        console.log(
          `[ProfileInfo] Detected field-level nested object field update. sectionKey: "${sectionKey}", fieldKey: "${fieldKey}", parentFieldKey: "${parentFieldKey}", nestedFieldKey: "${nestedFieldKey}"`
        );

        if (parentDef && parentDef.type === "object" && parentDef.fields) {
          // Build the complete object value from all field values
          const objValue =
            parentDef.value && typeof parentDef.value === "object"
              ? { ...parentDef.value }
              : {};
          objValue[nestedFieldKey] = newValue;
          parentDef.value = objValue;
          console.log(
            `[ProfileInfo] Synced parent nested object value for "${sectionKey}.${fieldKey}.fields.${parentFieldKey}":`,
            objValue
          );
        }
      }

      if (onDataChange) {
        onDataChange(next);
      }
      return next;
    });
  };

  const sections = Object.keys(localData || {});

  return (
    <Box
      sx={{ width: "100%", "& .MuiTypography-caption": { display: "none" } }}
    >
      {sections.map((sectionKey, idx) => {
        const section = localData[sectionKey] || {};
        const isSectionFieldSchema =
          section &&
          typeof section === "object" &&
          Object.prototype.hasOwnProperty.call(section, "type");
        const fields = isSectionFieldSchema ? [] : Object.keys(section);

        return (
          <Box key={sectionKey} sx={{ mb: 10, ml: { xs: 4, md: 10 } }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {humanizeLabel(sectionKey)}
            </Typography>
            <Divider sx={{ mb: 1 }} />

            {isSectionFieldSchema &&
              (() => {
                const def = section;
                const label = (def && def.label) || humanizeLabel(sectionKey);
                const desc = def && def.description;
                const ftype =
                  def && typeof def === "object" ? def.type : undefined;
                const isArrayType =
                  def && typeof def === "object" && def.type === "array";
                const isArraySchema = isArrayType && def.fields;
                const isObjectSchema =
                  def &&
                  typeof def === "object" &&
                  def.type === "object" &&
                  def.fields;

                if (editMode && ftype === "number") {
                  const value = getValueFromDef(def);
                  return (
                    <Box sx={{ py: 0.75 }}>
                      <FieldNumber
                        name={sectionKey}
                        schema={def}
                        value={value}
                        onChange={(v) => updateValueAtPath([sectionKey], v)}
                      />
                      {desc && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 0 }}
                        >
                          {desc}
                        </Typography>
                      )}
                    </Box>
                  );
                }
                if (editMode && ftype === "date") {
                  const value = getValueFromDef(def);
                  return (
                    <Box sx={{ py: 0.75 }}>
                      <FieldDate
                        name={sectionKey}
                        schema={def}
                        value={value}
                        onChange={(v) => updateValueAtPath([sectionKey], v)}
                      />
                      {desc && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 0 }}
                        >
                          {desc}
                        </Typography>
                      )}
                    </Box>
                  );
                }
                if (editMode && ftype === "select") {
                  const value = getValueFromDef(def);
                  return (
                    <Box sx={{ py: 0.75 }}>
                      <FieldSelect
                        name={sectionKey}
                        schema={def}
                        value={value}
                        onChange={(v) => updateValueAtPath([sectionKey], v)}
                      />
                      {desc && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 0 }}
                        >
                          {desc}
                        </Typography>
                      )}
                    </Box>
                  );
                }
                if (editMode && ftype === "boolean") {
                  const value = getValueFromDef(def);
                  return (
                    <Box sx={{ py: 0.75 }}>
                      <FieldBoolean
                        name={sectionKey}
                        schema={def}
                        value={value}
                        onChange={(v) => updateValueAtPath([sectionKey], v)}
                      />
                      {desc && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 0 }}
                        >
                          {desc}
                        </Typography>
                      )}
                    </Box>
                  );
                }
                if (editMode && ftype === "string") {
                  const value = getValueFromDef(def);
                  return (
                    <Box sx={{ py: 0.75 }}>
                      <FieldText
                        name={sectionKey}
                        schema={def}
                        value={value}
                        onChange={(v) => updateValueAtPath([sectionKey], v)}
                      />
                      {desc && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 0 }}
                        >
                          {desc}
                        </Typography>
                      )}
                    </Box>
                  );
                }

                if (isArrayType) {
                  const groupName =
                    def.groupName || def.group_name || def.group || "item";
                  // const itemType = def.item_type;
                  if (isArraySchema) {
                    if (editMode) {
                      const arrValue = Array.isArray(def.value)
                        ? def.value
                        : [];
                      return (
                        <FieldArray
                          name={sectionKey}
                          schema={def}
                          value={arrValue}
                          onChange={(next) =>
                            updateValueAtPath([sectionKey], next)
                          }
                        />
                      );
                    }
                    const innerKeys = Object.keys(def.fields || {});
                    const arrValue = Array.isArray(def.value)
                      ? def.value
                      : null;
                    return (
                      <Box sx={{ py: 0.75 }}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            alignItems: "baseline",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ minWidth: 220, fontWeight: 500 }}
                          >
                            {label} :
                          </Typography>
                          {/* <Typography variant="body2" color="text.primary">
                            Array of {humanizeLabel(groupName)}
                            {arrValue && arrValue.length
                              ? ` (${arrValue.length} items)`
                              : " (items structure below)"}
                          </Typography> */}
                        </Box>
                        {desc && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 0 }}
                          >
                            {desc}
                          </Typography>
                        )}
                        {arrValue && arrValue.length ? (
                          <Box sx={{ pl: 3, mt: 0.5 }}>
                            {arrValue.map((item, idx) => (
                              <Box
                                key={idx}
                                sx={{
                                  mb: 1,
                                  pl: 1,
                                  borderLeft: "1px solid",
                                  borderColor: "divider",
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: 500, mb: 0.5 }}
                                >
                                  {humanizeLabel(groupName)} {idx + 1}
                                </Typography>
                                {innerKeys.map((ik) => {
                                  const idef = def.fields[ik];
                                  const ilabel =
                                    (idef && idef.label) || humanizeLabel(ik);
                                  const idesc = idef && idef.description;
                                  // const itype = idef && idef.type;
                                  const ivalue = item && item[ik];
                                  return (
                                    <Box key={ik} sx={{ py: 0.35 }}>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          gap: 1,
                                          alignItems: "baseline",
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{ minWidth: 200 }}
                                        >
                                          {ilabel}:
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color="text.primary"
                                        >
                                          {ivalue === null ||
                                          ivalue === undefined ||
                                          ivalue === ""
                                            ? "—"
                                            : cleanString(String(ivalue))}
                                        </Typography>
                                      </Box>
                                      {idesc && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          {idesc}
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                })}
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Box sx={{ pl: 3, mt: 0.5 }}>
                            {innerKeys.map((ik) => {
                              const idef = def.fields[ik];
                              const ilabel =
                                (idef && idef.label) || humanizeLabel(ik);
                              const idesc = idef && idef.description;

                              return (
                                <Box key={ik} sx={{ py: 0.5 }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 1,
                                      alignItems: "baseline",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{ minWidth: 200 }}
                                    >
                                      {ilabel}:
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.primary"
                                    >
                                      —
                                    </Typography>
                                  </Box>
                                  {idesc && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {idesc}
                                    </Typography>
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        )}
                      </Box>
                    );
                  }
                  return (
                    <Box sx={{ py: 0.75 }}>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "baseline" }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ minWidth: 220, fontWeight: 500 }}
                        >
                          {label} :
                        </Typography>
                        {/* <Typography variant="body2" color="text.primary">
                          Array of{" "}
                          {itemType
                            ? humanizeLabel(itemType)
                            : humanizeLabel(groupName)}
                        </Typography> */}
                      </Box>
                      {desc && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 0 }}
                        >
                          {desc}
                        </Typography>
                      )}
                    </Box>
                  );
                }

                // Render object schemas in edit mode using FieldObject
                if (editMode && ftype === "object" && def.fields) {
                  return (
                    <Box sx={{ py: 0.75 }}>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "baseline" }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ minWidth: 220, fontWeight: 500 }}
                        >
                          {label}:
                        </Typography>
                      </Box>
                      {desc && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 0 }}
                        >
                          {desc}
                        </Typography>
                      )}
                      <FieldObject
                        name={sectionKey}
                        fields={def.fields}
                        editMode={editMode}
                        errors={errors}
                        path={[sectionKey]}
                        getValue={(ik) => {
                          const node =
                            (localData &&
                              localData[sectionKey] &&
                              localData[sectionKey].fields &&
                              localData[sectionKey].fields[ik]) ||
                            def.fields[ik];
                          return getValueFromDef(node);
                        }}
                        onChange={(ik, v) =>
                          updateValueAtPath([sectionKey, "fields", ik], v)
                        }
                      />
                    </Box>
                  );
                }

                if (isObjectSchema) {
                  const innerKeys = Object.keys(def.fields || {});
                  const objValue =
                    def && typeof def.value === "object" && def.value !== null
                      ? def.value
                      : null;
                  return (
                    <Box sx={{ py: 0.75 }}>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "baseline" }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ minWidth: 220, fontWeight: 500 }}
                        >
                          {label}:
                        </Typography>
                      </Box>
                      {desc && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 0 }}
                        >
                          {desc}
                        </Typography>
                      )}
                      <Box sx={{ pl: 3, mt: 0.5 }}>
                        {innerKeys.map((ik) => {
                          const idef = def.fields[ik];
                          const ilabel =
                            (idef && idef.label) || humanizeLabel(ik);
                          const idesc = idef && idef.description;
                          const itype = idef && idef.type;
                          const displayed =
                            objValue && ik in objValue
                              ? objValue[ik]
                              : idef &&
                                typeof idef === "object" &&
                                Object.prototype.hasOwnProperty.call(
                                  idef,
                                  "value"
                                )
                              ? idef.value
                              : undefined;
                          // Handle arrays inside object schemas
                          if (itype === "array") {
                            const isInnerArraySchema =
                              idef && idef.fields ? true : false;
                            if (editMode) {
                              const arrValue = Array.isArray(idef.value)
                                ? idef.value
                                : [];
                              if (isInnerArraySchema) {
                                return (
                                  <Box key={ik} sx={{ py: 0.5 }}>
                                    <FieldArray
                                      name={ik}
                                      schema={idef}
                                      value={arrValue}
                                      onChange={(next) =>
                                        updateValueAtPath(
                                          [sectionKey, "fields", ik],
                                          next
                                        )
                                      }
                                    />
                                  </Box>
                                );
                              }
                              // Primitive arrays (no fields)
                              return (
                                <Box key={ik} sx={{ py: 0.5 }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 1,
                                      alignItems: "baseline",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{ minWidth: 200 }}
                                    >
                                      {ilabel} :
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.primary"
                                    >
                                      {arrValue.length
                                        ? arrValue.join(", ")
                                        : "—"}
                                    </Typography>
                                  </Box>
                                  {idesc && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {idesc}
                                    </Typography>
                                  )}
                                </Box>
                              );
                            }
                            // View mode rendering
                            const arrValueView = Array.isArray(idef.value)
                              ? idef.value
                              : [];
                            if (isInnerArraySchema) {
                              const innerKeys2 = Object.keys(idef.fields || {});
                              return (
                                <Box key={ik} sx={{ py: 0.5 }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 1,
                                      alignItems: "baseline",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{ minWidth: 200 }}
                                    >
                                      {ilabel} :
                                    </Typography>
                                    {/* <Typography
                                      variant="body2"
                                      color="text.primary"
                                    >
                                      Array of{" "}
                                      {humanizeLabel(
                                        idef.groupName ||
                                          idef.group_name ||
                                          idef.group ||
                                          "item"
                                      )}
                                      {arrValueView.length
                                        ? ` (${arrValueView.length} items)`
                                        : ""}
                                    </Typography> */}
                                  </Box>
                                  <Box sx={{ pl: 3, mt: 0.5 }}>
                                    {arrValueView.map((item, idx) => (
                                      <Box key={idx} sx={{ mb: 0.5 }}>
                                        <Typography
                                          variant="subtitle2"
                                          sx={{ fontWeight: 500, mb: 0.25 }}
                                        >
                                          {humanizeLabel(
                                            idef.groupName ||
                                              idef.group_name ||
                                              idef.group ||
                                              "item"
                                          )}{" "}
                                          {idx + 1}
                                        </Typography>
                                        {innerKeys2.map((jk) => (
                                          <Box
                                            key={jk}
                                            sx={{ display: "flex", gap: 1 }}
                                          >
                                            <Typography
                                              variant="body2"
                                              sx={{ minWidth: 180 }}
                                            >
                                              {humanizeLabel(jk)}:
                                            </Typography>
                                            <Typography
                                              variant="body2"
                                              color="text.primary"
                                            >
                                              {item &&
                                              item[jk] !== undefined &&
                                              item[jk] !== null
                                                ? String(item[jk])
                                                : "—"}
                                            </Typography>
                                          </Box>
                                        ))}
                                      </Box>
                                    ))}
                                  </Box>
                                </Box>
                              );
                            }
                            // Primitive arrays view
                            return (
                              <Box key={ik} sx={{ py: 0.5 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 1,
                                    alignItems: "baseline",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ minWidth: 200 }}
                                  >
                                    {ilabel} :
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.primary"
                                  >
                                    {Array.isArray(idef.value) &&
                                    idef.value.length
                                      ? idef.value.join(", ")
                                      : "—"}
                                  </Typography>
                                </Box>
                                {idesc && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {idesc}
                                  </Typography>
                                )}
                              </Box>
                            );
                          }
                          if (editMode && itype === "number") {
                            const currentVal = getValueFromDef(idef);
                            return (
                              <Box key={ik} sx={{ py: 0.5 }}>
                                <FieldNumber
                                  name={ik}
                                  schema={idef}
                                  value={currentVal}
                                  onChange={(v) =>
                                    updateValueAtPath(
                                      [sectionKey, "fields", ik],
                                      v
                                    )
                                  }
                                />
                                {idesc && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {idesc}
                                  </Typography>
                                )}
                              </Box>
                            );
                          }
                          if (editMode && itype === "date") {
                            const currentVal = getValueFromDef(idef);
                            return (
                              <Box key={ik} sx={{ py: 0.5 }}>
                                <FieldDate
                                  name={ik}
                                  schema={idef}
                                  value={currentVal}
                                  onChange={(v) =>
                                    updateValueAtPath(
                                      [sectionKey, "fields", ik],
                                      v
                                    )
                                  }
                                />
                                {idesc && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {idesc}
                                  </Typography>
                                )}
                              </Box>
                            );
                          }
                          if (editMode && itype === "select") {
                            const currentVal = getValueFromDef(idef);
                            return (
                              <Box key={ik} sx={{ py: 0.5 }}>
                                <FieldSelect
                                  name={ik}
                                  schema={idef}
                                  value={currentVal}
                                  onChange={(v) =>
                                    updateValueAtPath(
                                      [sectionKey, "fields", ik],
                                      v
                                    )
                                  }
                                />
                                {idesc && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {idesc}
                                  </Typography>
                                )}
                              </Box>
                            );
                          }
                          if (editMode && itype === "boolean") {
                            const currentVal = getValueFromDef(idef);
                            return (
                              <Box key={ik} sx={{ py: 0.5 }}>
                                <FieldBoolean
                                  name={ik}
                                  schema={idef}
                                  value={currentVal}
                                  onChange={(v) =>
                                    updateValueAtPath(
                                      [sectionKey, "fields", ik],
                                      v
                                    )
                                  }
                                />
                                {idesc && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {idesc}
                                  </Typography>
                                )}
                              </Box>
                            );
                          }
                          if (editMode && itype === "string") {
                            const currentVal = getValueFromDef(idef);
                            const errorPath = `${sectionKey}.${ik}`;
                            const errorText = errors && errors[errorPath];
                            return (
                              <Box key={ik} sx={{ py: 0.5 }}>
                                <FieldText
                                  name={ik}
                                  schema={idef}
                                  value={currentVal}
                                  onChange={(v) =>
                                    updateValueAtPath(
                                      [sectionKey, "fields", ik],
                                      v
                                    )
                                  }
                                  errorText={errorText}
                                />
                                {idesc && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {idesc}
                                  </Typography>
                                )}
                              </Box>
                            );
                          }
                          // Handle nested objects within section-level object schemas
                          if (itype === "object" && idef.fields) {
                            const nestedObjValue =
                              displayed && typeof displayed === "object"
                                ? displayed
                                : null;
                            const nestedKeys = Object.keys(idef.fields || {});
                            if (editMode) {
                              return (
                                <Box key={ik} sx={{ py: 0.5 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500, mb: 0.5 }}
                                  >
                                    {ilabel}
                                  </Typography>
                                  <Box sx={{ pl: 2 }}>
                                    <FieldObject
                                      name={ik}
                                      fields={idef.fields}
                                      editMode={editMode}
                                      errors={errors}
                                      path={[sectionKey, ik]}
                                      getValue={(nestedKey) => {
                                        const node =
                                          (localData &&
                                            localData[sectionKey] &&
                                            localData[sectionKey].fields &&
                                            localData[sectionKey].fields[ik] &&
                                            localData[sectionKey].fields[ik]
                                              .fields &&
                                            localData[sectionKey].fields[ik]
                                              .fields[nestedKey]) ||
                                          idef.fields[nestedKey];
                                        return getValueFromDef(node);
                                      }}
                                      onChange={(nestedKey, v) =>
                                        updateValueAtPath(
                                          [
                                            sectionKey,
                                            "fields",
                                            ik,
                                            "fields",
                                            nestedKey,
                                          ],
                                          v
                                        )
                                      }
                                    />
                                  </Box>
                                </Box>
                              );
                            }
                            // View mode for nested objects
                            return (
                              <Box key={ik} sx={{ py: 0.5 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500, mb: 0.5 }}
                                >
                                  {ilabel}
                                </Typography>
                                <Box sx={{ pl: 2 }}>
                                  {nestedKeys.map((nk) => {
                                    const nDef = idef.fields[nk];
                                    const nLabel =
                                      (nDef && nDef.label) || humanizeLabel(nk);
                                    const nValue =
                                      nestedObjValue && nk in nestedObjValue
                                        ? nestedObjValue[nk]
                                        : nDef &&
                                          typeof nDef === "object" &&
                                          Object.prototype.hasOwnProperty.call(
                                            nDef,
                                            "value"
                                          )
                                        ? nDef.value
                                        : undefined;
                                    return (
                                      <Box
                                        key={nk}
                                        sx={{
                                          display: "flex",
                                          gap: 1,
                                          alignItems: "baseline",
                                          mb: 0.25,
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{ minWidth: 180 }}
                                        >
                                          {nLabel}:
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color="text.primary"
                                        >
                                          {nValue === null ||
                                          nValue === undefined ||
                                          nValue === ""
                                            ? "—"
                                            : String(nValue)}
                                        </Typography>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              </Box>
                            );
                          }
                          return (
                            <Box key={ik} sx={{ py: 0.5 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  alignItems: "baseline",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ minWidth: 200 }}
                                >
                                  {ilabel}:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.primary"
                                >
                                  {displayed === null ||
                                  displayed === undefined ||
                                  displayed === ""
                                    ? "—"
                                    : cleanString(String(displayed))}
                                </Typography>
                              </Box>
                              {idesc && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {idesc}
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                }

                return (
                  <Box sx={{ py: 0.75 }}>
                    <Box
                      sx={{ display: "flex", gap: 1, alignItems: "baseline" }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ minWidth: 220, fontWeight: 500 }}
                      >
                        {label}:
                      </Typography>
                      <Typography variant="body2" color="text.primary">
                        —
                      </Typography>
                    </Box>
                    {desc && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 0 }}
                      >
                        {desc}
                      </Typography>
                    )}
                  </Box>
                );
              })()}

            {!isSectionFieldSchema && fields.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No fields.
              </Typography>
            )}

            {!isSectionFieldSchema &&
              fields.map((fieldKey) => {
                const def = section[fieldKey];
                const label = (def && def.label) || humanizeLabel(fieldKey);
                const value = renderValue(def);
                const desc = def && def.description;
                const ftype =
                  def && typeof def === "object" ? def.type : undefined;
                const isArrayType =
                  def && typeof def === "object" && def.type === "array";
                const isArraySchema = isArrayType && def.fields;
                const isObjectSchema =
                  def &&
                  typeof def === "object" &&
                  def.type === "object" &&
                  def.fields;

                if (editMode && ftype === "number") {
                  const currentVal = getValueFromDef(def);
                  return (
                    <Box key={fieldKey} sx={{ py: 0.75 }}>
                      <FieldNumber
                        name={fieldKey}
                        schema={def}
                        value={currentVal}
                        onChange={(v) =>
                          updateValueAtPath([sectionKey, fieldKey], v)
                        }
                      />
                      {desc && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 0 }}
                        >
                          {desc}
                        </Typography>
                      )}
                    </Box>
                  );
                }
                if (editMode && ftype === "date") {
                  const currentVal = getValueFromDef(def);
                  return (
                    <Box key={fieldKey} sx={{ py: 0.75 }}>
                      <FieldDate
                        name={fieldKey}
                        schema={def}
                        value={currentVal}
                        onChange={(v) =>
                          updateValueAtPath([sectionKey, fieldKey], v)
                        }
                      />
                      {desc && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 0 }}
                        >
                          {desc}
                        </Typography>
                      )}
                    </Box>
                  );
                }
                if (editMode && ftype === "select") {
                  const currentVal = getValueFromDef(def);
                  return (
                    <Box key={fieldKey} sx={{ py: 0.75 }}>
                      <FieldSelect
                        name={fieldKey}
                        schema={def}
                        value={currentVal}
                        onChange={(v) =>
                          updateValueAtPath([sectionKey, fieldKey], v)
                        }
                      />
                      {desc && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 0 }}
                        >
                          {desc}
                        </Typography>
                      )}
                    </Box>
                  );
                }
                if (editMode && ftype === "boolean") {
                  const currentVal = getValueFromDef(def);
                  return (
                    <Box key={fieldKey} sx={{ py: 0.75 }}>
                      <FieldBoolean
                        name={fieldKey}
                        schema={def}
                        value={currentVal}
                        onChange={(v) =>
                          updateValueAtPath([sectionKey, fieldKey], v)
                        }
                      />
                      {desc && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 0 }}
                        >
                          {desc}
                        </Typography>
                      )}
                    </Box>
                  );
                }
                if (editMode && ftype === "string") {
                  const currentVal = getValueFromDef(def);
                  const errorPath = `${sectionKey}.${fieldKey}`;
                  const errorText = errors && errors[errorPath];
                  return (
                    <Box key={fieldKey} sx={{ py: 0.75 }}>
                      <FieldText
                        name={fieldKey}
                        schema={def}
                        value={currentVal}
                        onChange={(v) =>
                          updateValueAtPath([sectionKey, fieldKey], v)
                        }
                        errorText={errorText}
                      />
                      {desc && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 0 }}
                        >
                          {desc}
                        </Typography>
                      )}
                    </Box>
                  );
                }

                if (isArrayType) {
                  const groupName =
                    def.groupName || def.group_name || def.group || "item";
                  const itemType = def.item_type;

                  // If array has nested fields (array of objects)
                  if (isArraySchema) {
                    if (editMode) {
                      const arrValue = Array.isArray(def.value)
                        ? def.value
                        : [];
                      return (
                        <FieldArray
                          key={fieldKey}
                          name={fieldKey}
                          schema={def}
                          value={arrValue}
                          onChange={(next) =>
                            updateValueAtPath([sectionKey, fieldKey], next)
                          }
                        />
                      );
                    }
                    const innerKeys = Object.keys(def.fields || {});
                    const arrValue = Array.isArray(def.value)
                      ? def.value
                      : null;
                    return (
                      <Box key={fieldKey} sx={{ py: 0.75 }}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            alignItems: "baseline",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ minWidth: 220, fontWeight: 500 }}
                          >
                            {label} :
                          </Typography>
                          {/* <Typography variant="body2" color="text.primary">
                            Array of {humanizeLabel(groupName)}
                            {arrValue && arrValue.length
                              ? ` (${arrValue.length} items)`
                              : " (items structure below)"}
                          </Typography> */}
                        </Box>
                        {desc && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 0 }}
                          >
                            {desc}
                          </Typography>
                        )}
                        {arrValue && arrValue.length ? (
                          <Box sx={{ pl: 3, mt: 0.5 }}>
                            {arrValue.map((item, idx) => (
                              <Box
                                key={idx}
                                sx={{
                                  mb: 1,
                                  pl: 1,
                                  borderLeft: "1px solid",
                                  borderColor: "divider",
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: 500, mb: 0.5 }}
                                >
                                  {humanizeLabel(groupName)} {idx + 1}
                                </Typography>
                                {innerKeys.map((ik) => {
                                  const idef = def.fields[ik];
                                  const ilabel =
                                    (idef && idef.label) || humanizeLabel(ik);
                                  const idesc = idef && idef.description;

                                  const ivalue = item && item[ik];
                                  return (
                                    <Box key={ik} sx={{ py: 0.35 }}>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          gap: 1,
                                          alignItems: "baseline",
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{ minWidth: 200 }}
                                        >
                                          {ilabel}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color="text.primary"
                                        >
                                          {ivalue === null ||
                                          ivalue === undefined ||
                                          ivalue === ""
                                            ? "—"
                                            : String(ivalue)}
                                        </Typography>
                                      </Box>
                                      {idesc && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          {idesc}
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                })}
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Box sx={{ pl: 3, mt: 0.5 }}>
                            {innerKeys.map((ik) => {
                              const idef = def.fields[ik];
                              const ilabel =
                                (idef && idef.label) || humanizeLabel(ik);
                              const idesc = idef && idef.description;

                              return (
                                <Box key={ik} sx={{ py: 0.5 }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 1,
                                      alignItems: "baseline",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{ minWidth: 200 }}
                                    >
                                      {ilabel}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.primary"
                                    >
                                      —
                                    </Typography>
                                  </Box>
                                  {idesc && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {idesc}
                                    </Typography>
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        )}
                      </Box>
                    );
                  }

                  // Array with only item_type (e.g., array of strings)
                  return (
                    <Box key={fieldKey} sx={{ py: 0.75 }}>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "baseline" }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ minWidth: 220, fontWeight: 500 }}
                        >
                          {label} :
                        </Typography>
                        {/* <Typography variant="body2" color="text.primary">
                          Array of{" "}
                          {itemType
                            ? humanizeLabel(itemType)
                            : humanizeLabel(groupName)}
                        </Typography> */}
                      </Box>
                      {desc && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 0 }}
                        >
                          {desc}
                        </Typography>
                      )}
                    </Box>
                  );
                }

                if (editMode && ftype === "object" && def.fields) {
                  return (
                    <Box key={fieldKey} sx={{ py: 0.75 }}>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "baseline" }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ minWidth: 220, fontWeight: 500 }}
                        >
                          {label}:
                        </Typography>
                      </Box>
                      {desc && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 0 }}
                        >
                          {desc}
                        </Typography>
                      )}
                      <FieldObject
                        name={fieldKey}
                        fields={def.fields}
                        editMode={editMode}
                        errors={errors}
                        path={[sectionKey, fieldKey]}
                        getValue={(ik) => {
                          const node =
                            (localData &&
                              localData[sectionKey] &&
                              localData[sectionKey][fieldKey] &&
                              localData[sectionKey][fieldKey].fields &&
                              localData[sectionKey][fieldKey].fields[ik]) ||
                            def.fields[ik];
                          return getValueFromDef(node);
                        }}
                        onChange={(ik, v) =>
                          updateValueAtPath(
                            [sectionKey, fieldKey, "fields", ik],
                            v
                          )
                        }
                      />
                    </Box>
                  );
                }

                if (isObjectSchema) {
                  const innerKeys = Object.keys(def.fields || {});
                  const objValue =
                    def && typeof def.value === "object" && def.value !== null
                      ? def.value
                      : null;
                  return (
                    <Box key={fieldKey} sx={{ py: 0.75 }}>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "baseline" }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ minWidth: 220, fontWeight: 500 }}
                        >
                          {label}:
                        </Typography>
                      </Box>
                      {desc && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 0 }}
                        >
                          {desc}
                        </Typography>
                      )}
                      <Box sx={{ pl: 3, mt: 0.5 }}>
                        {innerKeys.map((ik) => {
                          const idef = def.fields[ik];
                          const ilabel =
                            (idef && idef.label) || humanizeLabel(ik);
                          const idesc = idef && idef.description;
                          const itype = idef && idef.type;
                          const displayed =
                            objValue && ik in objValue
                              ? objValue[ik]
                              : idef &&
                                typeof idef === "object" &&
                                Object.prototype.hasOwnProperty.call(
                                  idef,
                                  "value"
                                )
                              ? idef.value
                              : undefined;
                          // Handle arrays inside object schemas
                          if (itype === "array") {
                            const isInnerArraySchema =
                              idef && idef.fields ? true : false;
                            if (editMode) {
                              const arrValue = Array.isArray(idef.value)
                                ? idef.value
                                : [];
                              if (isInnerArraySchema) {
                                return (
                                  <Box key={ik} sx={{ py: 0.5 }}>
                                    <FieldArray
                                      name={ik}
                                      schema={idef}
                                      value={arrValue}
                                      onChange={(next) =>
                                        updateValueAtPath(
                                          [sectionKey, fieldKey, "fields", ik],
                                          next
                                        )
                                      }
                                    />
                                  </Box>
                                );
                              }
                              // Primitive arrays (no fields)
                              return (
                                <Box key={ik} sx={{ py: 0.5 }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 1,
                                      alignItems: "baseline",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{ minWidth: 200 }}
                                    >
                                      {ilabel} :
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.primary"
                                    >
                                      {arrValue.length
                                        ? arrValue.join(", ")
                                        : "—"}
                                    </Typography>
                                  </Box>
                                  {idesc && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {idesc}
                                    </Typography>
                                  )}
                                </Box>
                              );
                            }
                            // View mode rendering
                            const arrValueView = Array.isArray(idef.value)
                              ? idef.value
                              : [];
                            if (isInnerArraySchema) {
                              const innerKeys2 = Object.keys(idef.fields || {});
                              return (
                                <Box key={ik} sx={{ py: 0.5 }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 1,
                                      alignItems: "baseline",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{ minWidth: 200 }}
                                    >
                                      {ilabel} :
                                    </Typography>
                                    {/* <Typography
                                      variant="body2"
                                      color="text.primary"
                                    >
                                      Array of{" "}
                                      {humanizeLabel(
                                        idef.groupName ||
                                          idef.group_name ||
                                          idef.group ||
                                          "item"
                                      )}
                                      {arrValueView.length
                                        ? ` (${arrValueView.length} items)`
                                        : ""}
                                    </Typography> */}
                                  </Box>
                                  <Box sx={{ pl: 3, mt: 0.5 }}>
                                    {arrValueView.map((item, idx) => (
                                      <Box key={idx} sx={{ mb: 0.5 }}>
                                        <Typography
                                          variant="subtitle2"
                                          sx={{ fontWeight: 500, mb: 0.25 }}
                                        >
                                          {humanizeLabel(
                                            idef.groupName ||
                                              idef.group_name ||
                                              idef.group ||
                                              "item"
                                          )}{" "}
                                          {idx + 1}
                                        </Typography>
                                        {innerKeys2.map((jk) => (
                                          <Box
                                            key={jk}
                                            sx={{ display: "flex", gap: 1 }}
                                          >
                                            <Typography
                                              variant="body2"
                                              sx={{ minWidth: 180 }}
                                            >
                                              {humanizeLabel(jk)}:
                                            </Typography>
                                            <Typography
                                              variant="body2"
                                              color="text.primary"
                                            >
                                              {item &&
                                              item[jk] !== undefined &&
                                              item[jk] !== null
                                                ? String(item[jk])
                                                : "—"}
                                            </Typography>
                                          </Box>
                                        ))}
                                      </Box>
                                    ))}
                                  </Box>
                                </Box>
                              );
                            }
                            // Primitive arrays view
                            return (
                              <Box key={ik} sx={{ py: 0.5 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 1,
                                    alignItems: "baseline",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ minWidth: 200 }}
                                  >
                                    {ilabel} :
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.primary"
                                  >
                                    {Array.isArray(idef.value) &&
                                    idef.value.length
                                      ? idef.value.join(", ")
                                      : "—"}
                                  </Typography>
                                </Box>
                                {idesc && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {idesc}
                                  </Typography>
                                )}
                              </Box>
                            );
                          }
                          // Handle nested objects within object schemas
                          if (itype === "object" && idef.fields) {
                            const nestedObjValue =
                              displayed && typeof displayed === "object"
                                ? displayed
                                : null;
                            const nestedKeys = Object.keys(idef.fields || {});
                            if (editMode) {
                              return (
                                <Box key={ik} sx={{ py: 0.5 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500, mb: 0.5 }}
                                  >
                                    {ilabel}
                                  </Typography>
                                  <Box sx={{ pl: 2 }}>
                                    <FieldObject
                                      name={ik}
                                      fields={idef.fields}
                                      editMode={editMode}
                                      errors={errors}
                                      path={[sectionKey, fieldKey, ik]}
                                      getValue={(nestedKey) => {
                                        const node =
                                          (localData &&
                                            localData[sectionKey] &&
                                            localData[sectionKey][fieldKey] &&
                                            localData[sectionKey][fieldKey]
                                              .fields &&
                                            localData[sectionKey][fieldKey]
                                              .fields[ik] &&
                                            localData[sectionKey][fieldKey]
                                              .fields[ik].fields &&
                                            localData[sectionKey][fieldKey]
                                              .fields[ik].fields[nestedKey]) ||
                                          idef.fields[nestedKey];
                                        return getValueFromDef(node);
                                      }}
                                      onChange={(nestedKey, v) =>
                                        updateValueAtPath(
                                          [
                                            sectionKey,
                                            fieldKey,
                                            "fields",
                                            ik,
                                            "fields",
                                            nestedKey,
                                          ],
                                          v
                                        )
                                      }
                                    />
                                  </Box>
                                </Box>
                              );
                            }
                            // View mode for nested objects
                            return (
                              <Box key={ik} sx={{ py: 0.5 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500, mb: 0.5 }}
                                >
                                  {ilabel}
                                </Typography>
                                <Box sx={{ pl: 2 }}>
                                  {nestedKeys.map((nk) => {
                                    const nDef = idef.fields[nk];
                                    const nLabel =
                                      (nDef && nDef.label) || humanizeLabel(nk);
                                    const nValue =
                                      nestedObjValue && nk in nestedObjValue
                                        ? nestedObjValue[nk]
                                        : nDef &&
                                          typeof nDef === "object" &&
                                          Object.prototype.hasOwnProperty.call(
                                            nDef,
                                            "value"
                                          )
                                        ? nDef.value
                                        : undefined;
                                    return (
                                      <Box
                                        key={nk}
                                        sx={{
                                          display: "flex",
                                          gap: 1,
                                          alignItems: "baseline",
                                          mb: 0.25,
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{ minWidth: 180 }}
                                        >
                                          {nLabel}:
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color="text.primary"
                                        >
                                          {nValue === null ||
                                          nValue === undefined ||
                                          nValue === ""
                                            ? "—"
                                            : String(nValue)}
                                        </Typography>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              </Box>
                            );
                          }
                          return (
                            <Box key={ik} sx={{ py: 0.5 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  alignItems: "baseline",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ minWidth: 200 }}
                                >
                                  {ilabel}:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.primary"
                                >
                                  {displayed === null ||
                                  displayed === undefined ||
                                  displayed === ""
                                    ? "—"
                                    : String(displayed)}
                                </Typography>
                              </Box>
                              {idesc && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {idesc}
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                }

                return (
                  <Box key={fieldKey} sx={{ py: 0.75 }}>
                    <Box
                      sx={{ display: "flex", gap: 1, alignItems: "baseline" }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ minWidth: 220, fontWeight: 500 }}
                      >
                        {label}:
                      </Typography>
                      <Typography variant="body2" color="text.primary">
                        {value ? cleanString(value) : "—"}
                      </Typography>
                    </Box>
                    {desc && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 0 }}
                      >
                        {desc}
                      </Typography>
                    )}
                  </Box>
                );
              })}
          </Box>
        );
      })}
    </Box>
  );
}
